import { ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators'

import { getRangeFromDateFilter } from 'utils/dateUtils'
import { combineEpicsIsolated } from 'utils/epicRegistry'
import { getShareSuccessToastText } from 'utils/sentences'

import {
  selectSelectedCampaignIds,
  selectTimeFilter,
} from 'containers/Dashboard/Awareness/selectors'
import {
  INVESTIGATION_SHARE_CSV_PAGE_SIZE,
  POC_PAGE_SIZE,
} from 'containers/Investigation/ThreatLog/constants'
import { selectThreatLogFilters } from 'containers/Investigation/ThreatLog/selectors'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'

import { addToast } from '../Toasts/actions'

import {
  getSharingEmailsError,
  getSharingEmailsSuccess,
  shareReset,
  shareSubmitError,
  shareSubmitSuccess,
} from './actions'
import { FETCH_SHARING_EMAILS_ASYNC, SHARE_SUBMIT_ASYNC, SHARE_TYPE } from './constants'

const fetchSharingEmailsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_SHARING_EMAILS_ASYNC.INITIATE),
    switchMap((action) =>
      api.mailboxes
        .getEmails(
          state$.value.partnerOptions.selectedCompany?.value,
          action.payload.search,
          action.payload.includeMspUsers,
          action.payload.page,
          action.payload.pageSize
        )
        .pipe(
          map((response) => getSharingEmailsSuccess(response)),
          catchError((error) => of(epicsErrorHandler(getSharingEmailsError, error)))
        )
    )
  )

const getDateFilter = (dateFilter) =>
  Array.isArray(dateFilter) ? dateFilter.join(',') : dateFilter

const REQUIRED_SLICE = {
  [SHARE_TYPE.REPORT.MITIGATION]: 'dashboard',
  [SHARE_TYPE.REPORT.THREAT_PROTECTION]: 'dashboard',
  [SHARE_TYPE.REPORT.AWARENESS]: 'awarenessCenter',
  [SHARE_TYPE.REPORT.ANOMALIES]: 'dashboard',
  [SHARE_TYPE.REPORT.INCIDENT_SUMMARY_INCIDENTS]: 'dashboard',
  [SHARE_TYPE.REPORT.REMEDIATED_EMAILS]: 'resolutioncenter',
  [SHARE_TYPE.REPORT.REPORT_MAILBOXES]: 'mailboxes',
  [SHARE_TYPE.REPORT.THREAT_LOG_REPORT]: 'investigation',
  [SHARE_TYPE.REPORT.INVESTIGATION_REPORT]: 'investigation',
  [SHARE_TYPE.REPORT.POC_REPORT_LAST_LOG]: 'investigation',
}

export const shareSubmitEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(SHARE_SUBMIT_ASYNC.INITIATE),
    mergeMap((action) => {
      let res
      let dateFilter
      let campaignIds
      let reportTimes
      let mailboxesFilters
      let filters
      const companyFilterValue = selectCompanyFilter(state$.value)?.value
      let state = state$.value
      const body = {
        message: state.share.message,
        email: state.share.selectedProfilesProperties,
      }
      const requiredSlice =
        action.payload.shareType === SHARE_TYPE.REPORT.AWARENESS &&
        action.payload.customData?.campaign_id != null
          ? undefined
          : REQUIRED_SLICE[action.payload.shareType]
      if (requiredSlice && !state[requiredSlice]) {
        return of(
          epicsErrorHandler(
            shareSubmitError,
            new Error(`Cannot share ${action.payload.shareType}: ${requiredSlice} is not loaded`)
          )
        )
      }

      switch (action.payload.shareType) {
        case SHARE_TYPE.REPORT.MITIGATION:
          dateFilter = getDateFilter(state.dashboard.mitigation.flow.timeFilter)
          res = api.accounts.sendMitigationReport(companyFilterValue, {
            ...body,
            dateFilter,
          })
          break
        case SHARE_TYPE.REPORT.THREAT_PROTECTION:
          dateFilter = state.dashboard.threatProtection.flow.dateFilter
          res = api.accounts.sendThreatProtectionReport(companyFilterValue, {
            ...body,
            feDateRange: getRangeFromDateFilter(dateFilter),
          })
          break
        case SHARE_TYPE.REPORT.AWARENESS:
          res = api.accounts.sendCampaignReport(
            {
              ...body,
              campaign_id:
                action.payload.customData?.campaign_id ??
                state.awarenessCenter.campaignReport.data.id,
            },
            !!state.app.data.partner
          )
          break
        case SHARE_TYPE.REPORT.PHISHING_SIMULATOR:
          state = state$.value
          dateFilter = getRangeFromDateFilter(selectTimeFilter(state))
          campaignIds = selectSelectedCampaignIds(state)
          reportTimes = action.payload.customData
          res = api.accounts.sendPhishingSimulatorReport(companyFilterValue, {
            ...body,
            feDateRange: dateFilter,
            campaignIds,
            reportTimes,
          })
          break
        case SHARE_TYPE.REPORT.SCANBACK:
          res = api.accounts.sendScanbackReport(companyFilterValue, { ...body })
          break
        case SHARE_TYPE.REPORT.REPORTED_INCIDENTS:
          res = api.accounts.shareReportedIncidentsReport(companyFilterValue, body)
          break
        case SHARE_TYPE.REPORT.ANOMALIES:
          dateFilter = getDateFilter(state.dashboard.threatProtection.list.dateFilter)
          res = api.accounts.shareAnomaliesReport(companyFilterValue, {
            ...body,
            feDateRange: getRangeFromDateFilter(dateFilter),
            banner_type: state.dashboard.threatProtection.list.bannerTypeFilter.map(
              (elem) => elem.value
            ),
            search: state.dashboard.threatProtection.list.search,
          })
          break
        case SHARE_TYPE.REPORT.INCIDENT_SUMMARY_INCIDENTS:
          res = api.dashboard.getThreatProtectionIncidentsList(
            companyFilterValue,
            false,
            true,
            state.dashboard.threatProtection.list.page || 1,
            getRangeFromDateFilter(state.dashboard.threatProtection.list.dateFilter),
            state.dashboard.threatProtection.list.search,
            state.dashboard.threatProtection.list.incidentTypeFilter?.map((elem) => elem.label),
            state.dashboard.threatProtection.list.tagsFilter?.map((elem) => elem.label),
            state.dashboard.threatProtection.list.departmentFilter?.map((elem) => elem.label),
            state.dashboard.threatProtection.list.resolvedByFilter?.map((elem) => elem.label),
            state.dashboard.threatProtection.list.reportedByFilter?.map((elem) => elem.label),
            state.dashboard.threatProtection.list.resolutionTypeFilter?.map((elem) => elem.label),
            state.dashboard.threatProtection.list.threatTypeFilter?.map((elem) => elem.label),
            state.share.selectedProfilesProperties,
            state.share.message
          )
          break
        case SHARE_TYPE.REPORT.REMEDIATED_EMAILS:
          dateFilter = state.resolutioncenter.remediatedIncidents.dateFilter
          res = api.accounts.shareRemediatedEmailsReport(companyFilterValue, {
            ...body,
            shareCSV: true,
            feDateRange: getRangeFromDateFilter(dateFilter),
          })
          break
        case SHARE_TYPE.REPORT.REPORT_MAILBOXES:
          mailboxesFilters = {
            typeFilter: state.mailboxes.management.typeFilter,
            tagsFilter: state.mailboxes.management.tagsFilter.map((elem) => elem.value).join(','),
            segmentsFilter: state.mailboxes.management.segmentsFilter
              .map((elem) => elem.value)
              .join(','),
            protectedFilter: state.mailboxes.management.protectedFilter,
            riskLevelsFilter: state.mailboxes.management.riskLevelsFilter
              .map((elem) => elem.value)
              .join(','),
          }
          res = api.accounts.shareMailboxesReport(companyFilterValue, body, mailboxesFilters)
          break
        case SHARE_TYPE.REPORT.THREAT_LOG_REPORT: {
          filters = selectThreatLogFilters(state)
          body.page_size = INVESTIGATION_SHARE_CSV_PAGE_SIZE
          const { tagIds, ...restFilters } = filters
          res = api.threatLog.shareLogsCSV(restFilters, { ...body, tagIds })
          break
        }
        case SHARE_TYPE.REPORT.INVESTIGATION_REPORT: {
          filters = selectThreatLogFilters(state)
          body.page_size = INVESTIGATION_SHARE_CSV_PAGE_SIZE
          filters.page_number = 1
          const { tagIds, ...restFilters } = filters
          res = api.threatLog.shareInvestigationCSV(restFilters, { ...body, tagIds })
          break
        }
        case SHARE_TYPE.REPORT.POC_REPORT_LAST_LOG: {
          filters = selectThreatLogFilters(state)
          filters.page_size = POC_PAGE_SIZE
          filters.page_number = 1
          const { tagIds, ...restFilters } = filters
          res = api.threatLog.shareInvestigationPocCSV(restFilters, {
            ...body,
            tagIds,
          })
          break
        }
        default:
          throw new Error('Not supported SHARE_TYPE')
      }
      return res.pipe(
        mergeMap((response) =>
          of(
            shareSubmitSuccess(response),
            // callers that confirm the send themselves (e.g. the training report
            // header, which names the recipient count) opt out of the generic toast
            ...(action.payload.customData?.suppressSuccessToast
              ? []
              : [
                  addToast({
                    message: getShareSuccessToastText(action.payload.shareType),
                  }),
                ]),
            shareReset()
          )
        ),
        catchError((error) => of(epicsErrorHandler(shareSubmitError, error)))
      )
    })
  )

export default combineEpicsIsolated(shareSubmitEpic, fetchSharingEmailsEpic)
